/**
 * @fileoverview Pill: the app's one small rounded label, for a stage, a status, a mark or a count.
 *
 * The plant card's stage pill (PlantStageLine) is the pattern: a thumb-high
 * rounded pill with a hairline edge, semibold words, an optional icon before
 * them and a chevron after them when pressing it opens something. Every other
 * pill in the app (a plant row's stage, the weather chips on a card, a
 * "New to Grow" tag, the day on a public page's photo, a count on a button's
 * corner) is this one at another size and in another tone, instead of ten
 * hand-written copies that drifted apart.
 *
 * Three things set its look:
 * - size: md (2rem, the stage pill), sm (a compact label, 20px) or xs (a count);
 * - tone: its colours (neutral, outline, primary, solid, dashed, share, photo,
 *   photoLight, count, terracotta), or a gardener's own colour through theme
 *   (a stage's or a quick note's palette entry), where estimate draws the same
 *   colour paler with a dashed edge, as pencilled in;
 * - whether it is pressed: given onClick it is a button (type="button") with a
 *   hover and focus ring and a hit area a little larger than it looks (md and
 *   sm); without one it is a plain span.
 *
 * Callers that need a link in a pill's look (a router Link) use pillVariants
 * (styles/variants), as buttonVariants is used for a link that looks like a button.
 *
 * Where: PlantStageLine, PlantRow, PlantTile, QuickNoteChips,
 * PlantingDatesLine, PlantToolbar, PlantFilterSheet, LocationRow and
 * LocationCard, Today's answer chip, SeasonPage, Profile's stage and note
 * settings, the photo tray, the public garden pages, the share screen's
 * Preparing note and the subscribe page.
 * API: none.
 * Words: none; the caller gives the words.
 *
 * @module Pill
 */

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import type { ColorTheme } from '@/shared/types/theme';
import { pillVariants } from '@/shared/styles/variants';

/** What a pill is given: its size and tone, an optional icon and chevron, and what pressing it does. */
export interface PillProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof pillVariants> {
  /** A gardener's colour (a stage's or a quick note's palette entry): its badge colours, or, with estimate, the paler fill and dashed edge. Wins over tone's colours. */
  theme?: ColorTheme;
  /** Draws the pill as pencilled in: a dashed edge, and with a theme the paler fill of the same colour. */
  estimate?: boolean;
  /** Rings the pill in the page's ink: it is the one chosen, in a row of pills that are choices. */
  selected?: boolean;
  /** An icon before the words; give it as an element, it is sized by the pill. */
  icon?: React.ReactNode;
  /** A chevron after the words: pressing the pill opens something. */
  chevron?: boolean;
  /** Something after the words (a × that takes a filter off, say); given as an element, it is sized by the pill. */
  trailing?: React.ReactNode;
  /** Lets the words wrap onto a second line instead of being cut with an ellipsis (a plant card's chip, which a long day list in Spanish would cut off at 375px). */
  wrap?: boolean;
  /** Greys a pill that is pressed (a button) and stops it being pressed. */
  disabled?: boolean;
}

/**
 * The pill's colours from a gardener's palette entry.
 *
 * @param theme - The palette entry.
 * @param estimate - Whether the pill is an estimate: paler fill, dashed edge, the colour's own edge.
 * @returns The classes, for light and dark.
 */
function themeClasses(theme: ColorTheme, estimate: boolean): string {
  return estimate
    ? cn('border-dashed', theme.light.bg, theme.dark.bg, theme.light.text, theme.dark.text, theme.light.border, theme.dark.border)
    : cn(theme.light.badge, theme.dark.badge);
}

/**
 * A pill: a small rounded label, or a button in the same look when it is given onClick.
 *
 * Its words are cut with an ellipsis rather than wrapping (unless given wrap,
 * for a label that must be read whole), so a long stage in another language
 * cannot make a card's header taller. The other props
 * (title, aria-label, role, aria-checked...) pass through to the element.
 *
 * @param props - PillProps.
 */
export const Pill = React.forwardRef<HTMLElement, PillProps>(
  ({ className, size, tone, theme, estimate = false, selected = false, wrap = false, icon, chevron = false, trailing, disabled, onClick, children, ...rest }, ref) => {
    const pressable = Boolean(onClick);
    // The tone's own colours stand unless the pill is a gardener's colour or an estimate of a plain one.
    const looks = theme
      ? cn('border-transparent', themeClasses(theme, estimate))
      : estimate
        ? 'border-dashed border-muted-foreground/60 bg-transparent text-muted-foreground'
        : '';
    const hover = !pressable ? '' : tone === 'dashed' ? 'hover:border-primary hover:text-primary'
      : tone === 'outline' ? 'hover:bg-muted'
        : tone === 'share' ? '' : 'transition-[filter] hover:brightness-95';
    const classes = cn(
      pillVariants({ size, tone }),
      looks,
      // The one chosen is ringed in the page's ink, whatever its colour.
      selected && 'border-foreground',
      pressable && cn(
        // The hit area is a little larger than the pill looks.
        'relative before:absolute before:-inset-x-1 before:-inset-y-1 before:content-[\'\']',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        // A compact pill that is pressed is a little taller and wider than one that is read.
        size === 'sm' && 'min-h-7 px-2.5',
        hover,
        // A press darkens it a little, so a tap shows on a phone.
        'active:brightness-90',
      ),
      // A pill with an icon before its words, or a chevron after, has a little less padding on that side.
      (size ?? 'md') === 'md' && icon && 'pl-2.5',
      (size ?? 'md') === 'md' && (chevron || trailing) && 'pr-2',
      // Words that wrap make a taller shape: a softer corner than a full pill.
      wrap && 'rounded-2xl',
      className,
    );
    const inner = (
      <>
        {icon && <span className="inline-flex shrink-0 items-center" aria-hidden="true">{icon}</span>}
        {children !== undefined && children !== null && children !== false && <span className={cn('min-w-0', wrap ? 'whitespace-normal break-words' : 'truncate')}>{children}</span>}
        {trailing && <span className="inline-flex shrink-0 items-center" aria-hidden="true">{trailing}</span>}
        {chevron && <ChevronDown className="shrink-0 opacity-80" aria-hidden="true" />}
      </>
    );
    return pressable ? (
      <button
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        disabled={disabled}
        className={classes}
      >
        {inner}
      </button>
    ) : (
      <span {...rest} ref={ref as React.Ref<HTMLSpanElement>} className={classes}>{inner}</span>
    );
  },
);
Pill.displayName = 'Pill';
